import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { Canvas, Rect, Circle, Line, PencilBrush, Triangle, Ellipse, Polygon } from "fabric";
import MenuBar from "../common/MenuBar";
import * as fabric from "fabric";
import {
  Box, Button, Stack, IconButton, Tooltip, Typography,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  ToggleButton, ToggleButtonGroup, Chip, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Popover,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditIcon from "@mui/icons-material/Edit";
import GestureIcon from "@mui/icons-material/Gesture";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import HexagonOutlinedIcon from "@mui/icons-material/HexagonOutlined";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import BrushIcon from "@mui/icons-material/Brush";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import CreateIcon from "@mui/icons-material/Create";
import Fullscreen from "@mui/icons-material/Fullscreen";
import FullscreenExit from "@mui/icons-material/FullscreenExit";
import { useSnackbar } from "../Utility/UserMessageContext";
import AuthContext from "../Context/AuthProvider";
import { loadSVGFromString, util } from "fabric";
import { ANATOMY_IMAGES } from "./anatomyImages";
import { saveFormDataToLocal, clearFormDataFromLocal } from "../Utility/FormDataTempStorage";
import { showLoadingImage } from "../Utility/Utility";
import {
  getDiagrams,
  getDiagramById,
  createDiagram,
  updateDiagram,
  deleteDiagram,
} from "../API/DiagramApi";

const ZOOM_MIN = 0.05;
const ZOOM_MAX = 20;
const ZOOM_STEP = 0.1;

const COLORS = [
  "#000000", "#ffffff", "#ef4444", "#3b82f6", "#22c55e",
  "#f97316", "#a855f7", "#06b6d4", "#ec4899", "#eab308",
  "#78716c", "#64748b",
];

const STROKE_SIZES = [1, 2, 3, 5, 8, 12];

const SHAPE_OPTIONS = [
  { id: "rect", label: "Rectangle", icon: <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} /> },
  { id: "circle", label: "Circle", icon: <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} /> },
  { id: "ellipse", label: "Ellipse", icon: <RadioButtonUncheckedIcon sx={{ fontSize: 18, transform: "scaleX(1.4)" }} /> },
  { id: "line", label: "Line", icon: <RemoveOutlinedIcon sx={{ fontSize: 18 }} /> },
  { id: "triangle", label: "Triangle", icon: <ChangeHistoryIcon sx={{ fontSize: 18 }} /> },
  { id: "diamond", label: "Diamond", icon: <DiamondOutlinedIcon sx={{ fontSize: 18 }} /> },
  { id: "pentagon", label: "Pentagon", icon: <PentagonOutlinedIcon sx={{ fontSize: 18 }} /> },
  { id: "hexagon", label: "Hexagon", icon: <HexagonOutlinedIcon sx={{ fontSize: 18 }} /> },
  { id: "arrow", label: "Arrow Line", icon: <LinearScaleIcon sx={{ fontSize: 18 }} /> },
];

// Pen cursor — pencil SVG
const PEN_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%23000000' d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'/%3E%3C/svg%3E") 0 24, crosshair`;

// Square eraser cursor — white square with black border, sized to stroke
const makeEraserCursor = (strokeSize) => {
  const s = Math.max(12, strokeSize * 4);
  const half = Math.floor(s / 2);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'%3E%3Crect x='1' y='1' width='${s - 2}' height='${s - 2}' fill='rgba(255,255,255,0.8)' stroke='%23000' stroke-width='1.5'/%3E%3C/svg%3E") ${half} ${half}, cell`;
};

// ── Whiteboard metadata helpers ──
const WHITEBOARD_META_PREFIX = "tldraw-meta-";

export const getWhiteboardsForService = (serviceId) => {
  try {
    const raw = localStorage.getItem(WHITEBOARD_META_PREFIX + serviceId);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveWhiteboardMeta = (serviceId, list) => {
  localStorage.setItem(WHITEBOARD_META_PREFIX + serviceId, JSON.stringify(list));
};

function Drawing({ serviceId, uniqueId, readOnly = false, savedCanvasKey, diagramId }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const currentToolRef = useRef("draw");
  const startPointRef = useRef(null);
  const previewRef = useRef(null);
  const isDisposed = useRef(false);
  const isPanningRef = useRef(false);
  const lastPanPt = useRef(null);
  const spaceDown = useRef(false);

  // API diagram tracking
  const currentDiagramIdRef = useRef(diagramId || null);
  const autoSaveTimerRef = useRef(null);

  // drawing state
  const currentColorRef = useRef("#000000");
  const currentStrokeSizeRef = useRef(3);
  const [currentTool, setCurrentTool] = useState("draw");
  const [zoomPct, setZoomPct] = useState(50);
  const [color, setColor] = useState("#000000");
  const [strokeSize, setStrokeSize] = useState(3);
  const [activeShape, setActiveShape] = useState("rect");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [anatomySearch, setAnatomySearch] = useState("");
const [anatomyPreview, setAnatomyPreview] = useState(null);
const [recentAnatomy, setRecentAnatomy] = useState(() => {
  try { return JSON.parse(localStorage.getItem("anatomy-recent") || "[]"); } catch { return []; }
});

  // ── Save-name dialog state ──
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveNameError, setSaveNameError] = useState("");

  // dropdown menu state
  const [shapesAnchor, setShapesAnchor] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [sizeAnchor, setSizeAnchor] = useState(null);
  const shapesOpen = Boolean(shapesAnchor);
  const colorOpen = Boolean(colorAnchor);
  const sizeOpen = Boolean(sizeAnchor);

  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const [anatomyAnchor, setAnatomyAnchor] = useState(null);
  const anatomyOpen = Boolean(anatomyAnchor);

  /* ─── keep refs in sync with state ─── */
  useEffect(() => { currentColorRef.current = color; }, [color]);
  useEffect(() => { currentStrokeSizeRef.current = strokeSize; }, [strokeSize]);

  /* ─────────────────────────────────────────────
     Fullscreen
  ───────────────────────────────────────────── */
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  useEffect(() => {
    const fc = fabricRef.current;
    if (!fc || isDisposed.current) return;

    const t1 = setTimeout(() => {
      const container = canvasRef.current?.parentElement;
      if (container && !isDisposed.current) {
        fc.setDimensions({ width: container.clientWidth, height: container.clientHeight });
        fc.renderAll();
      }
    }, 50);

    const t2 = setTimeout(() => {
      const container = canvasRef.current?.parentElement;
      if (container && !isDisposed.current) {
        fc.setDimensions({ width: container.clientWidth, height: container.clientHeight });
        fc.renderAll();
      }
    }, 300);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isFullscreen]);

  /* ─────────────────────────────────────────────
     Zoom helpers
  ───────────────────────────────────────────── */
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const applyZoom = useCallback((nextZoom, point) => {
    const fc = fabricRef.current;
    if (!fc || isDisposed.current) return;
    const z = clamp(nextZoom, ZOOM_MIN, ZOOM_MAX);
    if (point) fc.zoomToPoint(point, z); else fc.setZoom(z);
    setZoomPct(Math.round(z * 100));
  }, []);

  const zoomIn = useCallback(() => {
    const fc = fabricRef.current; if (!fc) return;
    applyZoom(fc.getZoom() * (1 + ZOOM_STEP));
  }, [applyZoom]);

  const zoomOut = useCallback(() => {
    const fc = fabricRef.current; if (!fc) return;
    applyZoom(fc.getZoom() / (1 + ZOOM_STEP));
  }, [applyZoom]);

  const resetView = useCallback(() => {
    const fc = fabricRef.current; if (!fc) return;
    fc.setViewportTransform([1, 0, 0, 1, 0, 0]);
    setZoomPct(100);
  }, []);

  /* ─────────────────────────────────────────────
     API: Auto-save canvas data to existing diagram
  ───────────────────────────────────────────── */
  const saveCanvasToApi = useCallback(async (fc) => {
    if (!fc || isDisposed.current || !auth) return;
    if (!currentDiagramIdRef.current) return;

    try {
      const diagramJson = JSON.stringify(fc.toJSON());
      await updateDiagram(auth, {
        id: currentDiagramIdRef.current,
        diagramJson: diagramJson,
      });
    } catch (e) {
      console.error("Auto-save failed:", e);
    }
  }, [auth]);

  /* ─────────────────────────────────────────────
     API: Save click — direct save if existing, else open dialog
  ───────────────────────────────────────────── */
  const handleSaveClick = useCallback(async () => {
    if (currentDiagramIdRef.current) {
      const fc = fabricRef.current;
      if (!fc || isDisposed.current || !auth) return;
      setIsLoading(true);
      try {
        const diagramJson = JSON.stringify(fc.toJSON());
        const result = await updateDiagram(auth, {
          id: currentDiagramIdRef.current,
          diagramJson: diagramJson,
        });
        if (!result?.isSuccess) {
          showSnackbar("Failed to save whiteboard: " + result?.data, "error");
        } else {
          showSnackbar("Whiteboard saved successfully", "success");
          clearFormDataFromLocal(uniqueId);
        }
      } catch (e) {
        showSnackbar("An error occurred while saving.", "error");
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setSaveName("");
    setSaveNameError("");
    setSaveDialogOpen(true);
  }, [auth, uniqueId, showSnackbar]);

  /* ─────────────────────────────────────────────
     API: Named save confirm
  ───────────────────────────────────────────── */
  const handleSaveConfirm = useCallback(async () => {
    const name = saveName.trim();
    if (!name) {
      setSaveNameError("Please enter a name for this whiteboard.");
      return;
    }

    const fc = fabricRef.current;
    if (!fc || isDisposed.current || !auth) return;

    setIsLoading(true);
    try {
      const diagramJson = JSON.stringify(fc.toJSON());

      if (currentDiagramIdRef.current) {
        const result = await updateDiagram(auth, {
          id: currentDiagramIdRef.current,
          name: name,
          diagramJson: diagramJson,
          availedServiceId: serviceId,
        });

        if (!result?.isSuccess) {
          showSnackbar("Failed to save whiteboard: " + result?.data, "error");
          return;
        }
      } else {
        const result = await createDiagram(auth, {
          name: name,
          diagramJson: diagramJson,
          availedServiceId: serviceId,
        });

        if (!result?.isSuccess) {
          showSnackbar("Failed to save whiteboard: " + result?.data, "error");
          return;
        }

        currentDiagramIdRef.current = result.data?.id ?? result.data;
      }

      setSaveDialogOpen(false);
      setSaveName("");
      setSaveNameError("");
      clearFormDataFromLocal(uniqueId);
      // Canvas clear NAHI hoga
      showSnackbar(`Whiteboard "${name}" saved successfully`, "success");
    } catch (e) {
      console.error("Save error:", e);
      showSnackbar("An error occurred while saving.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [saveName, auth, serviceId, uniqueId, showSnackbar]);

  /* ─────────────────────────────────────────────
     API: Load diagram on mount (if diagramId prop passed)
  ───────────────────────────────────────────── */
  const loadDiagramFromApi = useCallback(async (fc) => {
    if (!auth || !diagramId) return;

    setIsLoading(true);
    try {
      const result = await getDiagramById(auth, diagramId);
      if (result?.isSuccess && result.data?.diagramJson) {
        const parsed = JSON.parse(result.data.diagramJson);
        await fc.loadFromJSON(parsed);
        if (!isDisposed.current) fc.renderAll();
        currentDiagramIdRef.current = diagramId;
      } else if (!result?.isSuccess) {
        showSnackbar("Failed to load diagram: " + result?.data, "error");
      }
    } catch (e) {
      console.error("Load diagram failed:", e);
      showSnackbar("Failed to load diagram.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [auth, diagramId, showSnackbar]);

  /* ─────────────────────────────────────────────
     Clear canvas
  ───────────────────────────────────────────── */
  const handleClear = useCallback(() => {
    const fc = fabricRef.current;
    if (!fc || isDisposed.current) return;
    fc.clear();
    fc.backgroundColor = "#fff";
    fc.renderAll();
    clearFormDataFromLocal(uniqueId);
    currentToolRef.current = "draw";
    setCurrentTool("draw");
    fc.isDrawingMode = true;
    applyBrush(fc, "draw");
  }, [uniqueId]);

  /* ─────────────────────────────────────────────
     Brush helper
  ───────────────────────────────────────────── */
  const applyBrush = (fc, tool) => {
    const b = new PencilBrush(fc);
    b.width = currentStrokeSizeRef.current;
    b.color = currentColorRef.current;
    fc.freeDrawingBrush = b;
  };

  /* ─────────────────────────────────────────────
     Polyline helpers
  ───────────────────────────────────────────── */
  const makePolyPoints = (cx, cy, rx, ry, sides) => {
    const pts = [];
    for (let i = 0; i < sides; i++) {
      const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
      pts.push({ x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) });
    }
    return pts;
  };

  const makeArrowPoints = (sx, sy, ex, ey) => {
    const headLen = 18;
    const angle = Math.atan2(ey - sy, ex - sx);
    return {
      shaft: [sx, sy, ex, ey],
      head: [
        ex, ey,
        ex - headLen * Math.cos(angle - Math.PI / 6),
        ey - headLen * Math.sin(angle - Math.PI / 6),
        ex - headLen * Math.cos(angle + Math.PI / 6),
        ey - headLen * Math.sin(angle + Math.PI / 6),
      ],
    };
  };

  /* ─────────────────────────────────────────────
     Build shape
  ───────────────────────────────────────────── */
  const buildShape = useCallback((tool, sx, sy, ex, ey, opts = {}) => {
    const c = currentColorRef.current;
    const sw = currentStrokeSizeRef.current;
    const base = { fill: "transparent", stroke: c, strokeWidth: sw, ...opts };
    const minX = Math.min(sx, ex), minY = Math.min(sy, ey);
    const w = Math.abs(ex - sx), h = Math.abs(ey - sy);
    const cx = (sx + ex) / 2, cy = (sy + ey) / 2;

    switch (tool) {
      case "rect":
        return new Rect({ left: minX, top: minY, width: w, height: h, ...base });
      case "circle": {
        const r = Math.max(w, h) / 2;
        return new Circle({ left: cx - r, top: cy - r, radius: r, ...base });
      }
      case "ellipse":
        return new Ellipse({ left: minX, top: minY, rx: w / 2, ry: h / 2, ...base });
      case "line":
        return new Line([sx, sy, ex, ey], { stroke: c, strokeWidth: sw, ...opts });
      case "triangle": {
        // FIX: Points relative to bounding box origin so shape always draws
        // from mouse-down corner toward mouse-up corner correctly
        const pts = [{ x: w / 2, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        return new Polygon(pts, { left: minX, top: minY, ...base });
      }
      case "diamond": {
        // FIX: Points relative to bounding box origin
        const pts = [{ x: w / 2, y: 0 }, { x: w, y: h / 2 }, { x: w / 2, y: h }, { x: 0, y: h / 2 }];
        return new Polygon(pts, { left: minX, top: minY, ...base });
      }
      case "pentagon": {
        const pts = makePolyPoints(0, 0, w / 2, h / 2, 5).map(p => ({ x: p.x + w / 2, y: p.y + h / 2 }));
        return new Polygon(pts, { left: minX, top: minY, ...base });
      }
      case "hexagon": {
        const pts = makePolyPoints(0, 0, w / 2, h / 2, 6).map(p => ({ x: p.x + w / 2, y: p.y + h / 2 }));
        return new Polygon(pts, { left: minX, top: minY, ...base });
      }
      case "arrow": {
        const { shaft, head } = makeArrowPoints(sx, sy, ex, ey);
        const group = [];
        group.push(new Line(shaft, { stroke: c, strokeWidth: sw, selectable: false, evented: false, ...opts }));
        group.push(new Polygon(
          [{ x: head[0], y: head[1] }, { x: head[2], y: head[3] }, { x: head[4], y: head[5] }],
          { fill: c, stroke: c, strokeWidth: 1, selectable: false, evented: false, ...opts }
        ));
        return group;
      }
      default:
        return null;
    }
  }, []);

  const SHAPE_TOOLS = new Set(["rect", "circle", "ellipse", "line", "triangle", "diamond", "pentagon", "hexagon", "arrow"]);

  /* ─────────────────────────────────────────────
     Init fabric canvas
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (!canvasRef.current) return;
    isDisposed.current = false;

    const initTimer = setTimeout(() => {
      if (!canvasRef.current || isDisposed.current) return;
      const container = canvasRef.current.parentElement;
      if (!container) return;

      const w = container.clientWidth || 800;
      const h = container.clientHeight || 600;

      const fc = new Canvas(canvasRef.current, {
        width: w, height: h,
        backgroundColor: "#fff",
        isDrawingMode: !readOnly,
        stopContextMenu: true,
      });
      fabricRef.current = fc;

      const brush = new PencilBrush(fc);
      brush.width = 3; brush.color = "#000000";
      fc.freeDrawingBrush = brush;

      if (!readOnly) {
        fc.freeDrawingCursor = PEN_CURSOR;
      }

      loadDiagramFromApi(fc);

      /* ── mouse down ── */
      const onDown = (opt) => {
        if (opt.e?.button === 1) { opt.e.preventDefault(); isPanningRef.current = true; lastPanPt.current = { x: opt.e.clientX, y: opt.e.clientY }; return; }
        if (spaceDown.current) { isPanningRef.current = true; lastPanPt.current = { x: opt.e.clientX, y: opt.e.clientY }; return; }
        if (opt.target) { startPointRef.current = null; return; }
        const pt = opt.scenePoint; if (!pt) return;
        startPointRef.current = { x: pt.x, y: pt.y };
      };

      /* ── mouse move ── */
      const onMove = (opt) => {
        if (isPanningRef.current && lastPanPt.current) {
          const dx = opt.e.clientX - lastPanPt.current.x;
          const dy = opt.e.clientY - lastPanPt.current.y;
          lastPanPt.current = { x: opt.e.clientX, y: opt.e.clientY };
          const vpt = fc.viewportTransform.slice(); vpt[4] += dx; vpt[5] += dy;
          fc.setViewportTransform(vpt); fc.requestRenderAll(); return;
        }
        const tool = currentToolRef.current;
        if (!startPointRef.current) return;
        if (!SHAPE_TOOLS.has(tool)) return;
        const pt = opt.scenePoint; if (!pt) return;
        const { x: sx, y: sy } = startPointRef.current;

        if (previewRef.current) {
          [].concat(previewRef.current).forEach(s => fc.remove(s));
          previewRef.current = null;
        }

        const previewOpts = { selectable: false, evented: false, opacity: 0.55 };
        const shape = buildShape(tool, sx, sy, pt.x, pt.y, previewOpts);
        if (shape) {
          const shapes = [].concat(shape);
          shapes.forEach(s => fc.add(s));
          previewRef.current = shape;
          fc.renderAll();
        }
      };

      /* ── mouse up ── */
      const onUp = (opt) => {
        if (isPanningRef.current) { isPanningRef.current = false; lastPanPt.current = null; return; }
        if (!startPointRef.current) return;
        const tool = currentToolRef.current;
        if (!SHAPE_TOOLS.has(tool)) { startPointRef.current = null; return; }
        const pt = opt.scenePoint; if (!pt) { startPointRef.current = null; return; }
        const { x: sx, y: sy } = startPointRef.current;
        const ex = pt.x, ey = pt.y;
        startPointRef.current = null;

        if (previewRef.current) {
          [].concat(previewRef.current).forEach(s => fc.remove(s));
          previewRef.current = null;
        }
        if (Math.abs(ex - sx) < 3 && Math.abs(ey - sy) < 3) return;

        const shape = buildShape(tool, sx, sy, ex, ey);
        if (shape) {
          [].concat(shape).forEach(s => fc.add(s));
          fc.renderAll();
          if (!readOnly) saveFormDataToLocal(uniqueId, { dirty: true });
          saveCanvasToApi(fc);
        }
      };

      fc.on("mouse:down", onDown);
      fc.on("mouse:move", onMove);
      fc.on("mouse:up", onUp);

      // ── ERASER ──
      const onEraserDown = (opt) => {
        if (currentToolRef.current !== "eraser") return;
        startPointRef.current = null;
      };

      const onEraserMove = (opt) => {
        if (currentToolRef.current !== "eraser") return;
        if (!opt.e.buttons) return;
        const pt = opt.scenePoint;
        if (!pt) return;

        const r = Math.max(currentStrokeSizeRef.current * 4, 12);
        const toRemove = [];

        fc.getObjects().forEach(obj => {
          const objCanvas = document.createElement("canvas");
          const br = obj.getBoundingRect();

          if (
            pt.x < br.left - r || pt.x > br.left + br.width + r ||
            pt.y < br.top - r || pt.y > br.top + br.height + r
          ) return;

          const padding = r + 4;
          objCanvas.width = Math.ceil(br.width) + padding * 2;
          objCanvas.height = Math.ceil(br.height) + padding * 2;
          const ctx = objCanvas.getContext("2d");

          ctx.translate(-br.left + padding, -br.top + padding);
          const zoom = fc.getZoom();
          ctx.scale(zoom, zoom);
          const vpt = fc.viewportTransform;
          ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
          ctx.setTransform(1, 0, 0, 1, -br.left + padding, -br.top + padding);
          obj.render(ctx);

          const localX = Math.round(pt.x - br.left + padding);
          const localY = Math.round(pt.y - br.top + padding);

          let hit = false;
          for (let dx = -r; dx <= r && !hit; dx += 2) {
            for (let dy = -r; dy <= r && !hit; dy += 2) {
              if (dx * dx + dy * dy > r * r) continue;
              try {
                const pixel = ctx.getImageData(localX + dx, localY + dy, 1, 1).data;
                if (pixel[3] > 30) hit = true;
              } catch (_) { }
            }
          }

          if (hit) toRemove.push(obj);
        });

        if (toRemove.length > 0) {
          toRemove.forEach(obj => fc.remove(obj));
          fc.renderAll();
          if (!readOnly) saveFormDataToLocal(uniqueId, { dirty: true });
          saveCanvasToApi(fc);
        }
      };

      fc.on("mouse:down", onEraserDown);
      fc.on("mouse:move", onEraserMove);

      fc.on("path:created", () => {
        if (!readOnly) saveFormDataToLocal(uniqueId, { dirty: true });
        saveCanvasToApi(fc);
      });
      fc.on("object:modified", () => {
        if (!readOnly) saveFormDataToLocal(uniqueId, { dirty: true });
        saveCanvasToApi(fc);
      });

      /* ── wheel zoom / pan ── */
      const wrapEl = fc.getElement()?.parentElement;
      const onWheel = (e) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          const delta = e.deltaY > 0 ? -(1 + ZOOM_STEP) : (1 + ZOOM_STEP);
          const z = clamp(delta > 0 ? fc.getZoom() * delta : fc.getZoom() / (-delta), ZOOM_MIN, ZOOM_MAX);
          fc.zoomToPoint({ x: e.offsetX, y: e.offsetY }, z);
          setZoomPct(Math.round(z * 100));
        } else {
          const vpt = fc.viewportTransform.slice(); vpt[4] -= e.deltaX; vpt[5] -= e.deltaY;
          fc.setViewportTransform(vpt); fc.requestRenderAll();
        }
      };
      if (wrapEl) wrapEl.addEventListener("wheel", onWheel, { passive: false });

      /* ── keyboard ── */
      const onKeyDown = (e) => {
        if (e.code === "Space" && !e.target.matches("input,textarea")) { e.preventDefault(); spaceDown.current = true; if (fc.wrapperEl) fc.wrapperEl.style.cursor = "grab"; }
        if (e.ctrlKey && (e.key === "=" || e.key === "+")) { e.preventDefault(); zoomIn(); }
        if (e.ctrlKey && e.key === "-") { e.preventDefault(); zoomOut(); }
        if (e.ctrlKey && e.key === "0") { e.preventDefault(); resetView(); }
      };
      const onKeyUp = (e) => {
        if (e.code === "Space") { spaceDown.current = false; isPanningRef.current = false; if (fc.wrapperEl) fc.wrapperEl.style.cursor = ""; }
      };
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("keyup", onKeyUp);

      /* ── resize ── */
      const onResize = () => {
        const cont = canvasRef.current?.parentElement;
        if (!cont || isDisposed.current || !fabricRef.current) return;
        try {
          fabricRef.current.setDimensions({ width: cont.clientWidth, height: cont.clientHeight });
          fabricRef.current.renderAll();
        } catch (_) { }
      };
      window.addEventListener("resize", onResize);

      // ── Periodic auto-save every 30s ──
      const saveTimer = setInterval(() => {
        if (!readOnly && !isDisposed.current) saveCanvasToApi(fc);
      }, 30000);
      autoSaveTimerRef.current = saveTimer;

      return () => {
        isDisposed.current = true;
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("keyup", onKeyUp);
        window.removeEventListener("resize", onResize);
        clearInterval(saveTimer);
        if (wrapEl) wrapEl.removeEventListener("wheel", onWheel);
        fc.off("mouse:down", onDown);
        fc.off("mouse:move", onMove);
        fc.off("mouse:up", onUp);
        fc.off("mouse:move", onEraserMove);
        fc.off("mouse:down", onEraserDown);
        try { fc.dispose(); } catch (_) { }
        fabricRef.current = null;
      };
    }, 50);

    return () => {
      clearTimeout(initTimer);
      isDisposed.current = true;
      if (fabricRef.current) {
        try { fabricRef.current.dispose(); } catch (_) { }
        fabricRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  /* ─────────────────────────────────────────────
     Update brush color / size live
  ───────────────────────────────────────────── */
  useEffect(() => {
    const fc = fabricRef.current; if (!fc) return;
    if (fc.isDrawingMode && fc.freeDrawingBrush) {
      if (currentToolRef.current !== "eraser") fc.freeDrawingBrush.color = color;
    }
  }, [color]);

  useEffect(() => {
    const fc = fabricRef.current; if (!fc) return;
    if (fc.isDrawingMode && fc.freeDrawingBrush) {
      const tool = currentToolRef.current;
      if (tool !== "eraser") {
        fc.freeDrawingBrush.width = strokeSize;
      }
    }
    if (currentToolRef.current === "eraser") {
      const cur = makeEraserCursor(strokeSize);
      const fc2 = fabricRef.current;
      if (fc2) {
        fc2.defaultCursor = cur;
        if (fc2.wrapperEl) fc2.wrapperEl.style.cursor = cur;
      }
    }
  }, [strokeSize]);

  /* ─────────────────────────────────────────────
     Change tool
  ───────────────────────────────────────────── */
  const changeTool = useCallback((tool) => {
    if (readOnly) return;
    currentToolRef.current = tool;
    setCurrentTool(tool);
    const fc = fabricRef.current; if (!fc) return;

    if (previewRef.current) { [].concat(previewRef.current).forEach(s => fc.remove(s)); previewRef.current = null; }
    startPointRef.current = null;

    if (tool === "draw") {
      fc.isDrawingMode = true;
      applyBrush(fc, tool);
      fc.freeDrawingCursor = PEN_CURSOR;
      fc.defaultCursor = PEN_CURSOR;
      if (fc.wrapperEl) fc.wrapperEl.style.cursor = PEN_CURSOR;
    } else if (tool === "eraser") {
      fc.isDrawingMode = false;
      fc.selection = false;
      const eraserCursor = makeEraserCursor(currentStrokeSizeRef.current);
      fc.defaultCursor = eraserCursor;
      if (fc.wrapperEl) fc.wrapperEl.style.cursor = eraserCursor;
      fc.forEachObject(o => { o.selectable = false; o.evented = false; });
    } else if (SHAPE_TOOLS.has(tool) || tool === "select") {
      fc.isDrawingMode = false;
      fc.selection = tool === "select";
      fc.defaultCursor = tool === "select" ? "move" : "crosshair";
      if (fc.wrapperEl) fc.wrapperEl.style.cursor = tool === "select" ? "move" : "crosshair";
      fc.forEachObject(o => { o.selectable = tool === "select"; o.evented = tool === "select"; });
    }
  }, [readOnly]);

  /* ─────────────────────────────────────────────
     Handle shape selection from dropdown
  ───────────────────────────────────────────── */
  const handleShapeSelect = (shapeId) => {
    setActiveShape(shapeId);
    setShapesAnchor(null);
    changeTool(shapeId);
  };

  const activeShapeOption = SHAPE_OPTIONS.find(s => s.id === activeShape) || SHAPE_OPTIONS[0];
  const isShapeActive = SHAPE_TOOLS.has(currentTool);

  const handleAnatomyInsert = useCallback(async (item) => {
    setAnatomyAnchor(null);
    const fc = fabricRef.current;
    if (!fc || isDisposed.current) return;

    try {
      let svgString = item.svg;

      if (!svgString && item.svgUrl) {
        const response = await fetch(item.svgUrl);
        svgString = await response.text();
      }

      if (!svgString) {
        showSnackbar("No SVG source available.", "error");
        return;
      }

      const { objects, options } = await loadSVGFromString(svgString);
      const group = util.groupSVGElements(objects, options);

      const MAX_DIM = 400;
      const naturalW = group.width || 300;
      const naturalH = group.height || 300;
      const scale = Math.min(1, MAX_DIM / Math.max(naturalW, naturalH));
      group.scale(scale);

      const vpt = fc.viewportTransform;
      const zoom = fc.getZoom();
      // FIX: Horizontal center, 30% from top so anatomy lands below toolbar
      // without floating to the very top of the visible canvas area
      const centerX = (fc.getWidth() / 2 - vpt[4]) / zoom;
      const centerY = (fc.getHeight() * 0.50 - vpt[5]) / zoom;

      group.set({
        left: centerX - group.getScaledWidth() / 2,
        top: centerY,
        selectable: true,
        evented: true,
      });

      fc.add(group);
      fc.setActiveObject(group);
      fc.renderAll();
      if (!readOnly) saveFormDataToLocal(uniqueId, { dirty: true });
      saveCanvasToApi(fc);
      // FIX: Auto-switch to select tool so anatomy is immediately movable/resizable
      changeTool("select");
      setRecentAnatomy(prev => {
  const updated = [item, ...prev.filter(r => r.id !== item.id)].slice(0, 3);
  localStorage.setItem("anatomy-recent", JSON.stringify(updated));
  return updated;
});

    } catch (err) {
      console.error("Anatomy SVG insert failed:", err);
      showSnackbar("Failed to insert anatomy image.", "error");
    }
  }, [saveCanvasToApi, showSnackbar, readOnly, uniqueId, changeTool]);

  /* ─────────────────────────────────────────────
     Render
  ───────────────────────────────────────────── */
  return (
    <>
      <Box
        sx={
          isFullscreen
            ? {
              position: "fixed", inset: 0, zIndex: 1300,
              background: "#e8eaed",
              display: "flex", flexDirection: "column",
              overflow: "hidden",
              width: "100vw",
              height: "100vh",
            }
            : { display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }
        }
      >
        {/* ── MenuBar ── */}
        {!readOnly && (
          <MenuBar
            showSave
            onSave={handleSaveClick}
            saveLabel="Save"
            saveTooltip="Save whiteboard with a name"
            showDelete
            onDelete={handleClear}
            deleteLabel="Clear Canvas"
            deleteTooltip="Erase everything on the canvas"
          />
        )}

        {/* ── Toolbar ── */}
        {!readOnly && (
          <Box sx={{
            px: 1, py: 1,
            background: "#f8f9fb",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}>

            {/* ── GROUP: Draw tools ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, background: "#fff", borderRadius: 2, border: "1px solid #e0e0e0", p: 0.3 }}>
              {/* Pen */}
              <Tooltip title="Pen (freehand)" arrow>
                <IconButton
                  size="small"
                  onClick={() => changeTool("draw")}
                  sx={{
                    color: currentTool === "draw" ? "#1976d2" : "#666",
                    background: currentTool === "draw" ? "#e3f2fd" : "transparent",
                    borderRadius: 1.5, width: 36, height: 32,
                    "&:hover": { background: "#f0f0f0" },
                  }}
                >
                  <CreateIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              {/* Eraser */}
              <Tooltip title="Eraser" arrow>
                <IconButton
                  size="small"
                  onClick={() => changeTool("eraser")}
                  sx={{
                    color: currentTool === "eraser" ? "#d32f2f" : "#666",
                    background: currentTool === "eraser" ? "#ffebee" : "transparent",
                    borderRadius: 1.5, width: 36, height: 32,
                    "&:hover": { background: "#f0f0f0" },
                  }}
                >
                  <CropSquareIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              {/* Select */}
              <Tooltip title="Select & move objects" arrow>
                <IconButton
                  size="small"
                  onClick={() => changeTool("select")}
                  sx={{
                    color: currentTool === "select" ? "#7b1fa2" : "#666",
                    background: currentTool === "select" ? "#f3e5f5" : "transparent",
                    borderRadius: 1.5, width: 36, height: 32,
                    "&:hover": { background: "#f0f0f0" },
                  }}
                >
                  <OpenWithIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* ── GROUP: Shapes dropdown ── */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                size="small"
                onClick={(e) => setShapesAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                startIcon={activeShapeOption.icon}
                sx={{
                  color: isShapeActive ? "#1976d2" : "#333",
                  background: isShapeActive ? "#e3f2fd" : "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 500,
                  px: 1.5, py: 0.6,
                  minWidth: 110,
                  "&:hover": { background: "#f5f5f5", borderColor: "#ccc" },
                }}
              >
                {activeShapeOption.label}
              </Button>

              <Menu
                anchorEl={shapesAnchor}
                open={shapesOpen}
                onClose={() => setShapesAnchor(null)}
                PaperProps={{
                  sx: {
                    background: "#fff", border: "1px solid #e0e0e0", borderRadius: 2,
                    mt: 0.5, minWidth: 170,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "& .MuiMenuItem-root": {
                      fontSize: 13, borderRadius: 1, mx: 0.5,
                      "&:hover": { background: "#f5f5f5" },
                      "&.Mui-selected": { background: "#e3f2fd", color: "#1976d2" },
                    },
                  },
                }}
              >
                {SHAPE_OPTIONS.map((s) => (
                  <MenuItem key={s.id} selected={activeShape === s.id} onClick={() => handleShapeSelect(s.id)} dense>
                    <ListItemIcon sx={{ minWidth: 32 }}>{s.icon}</ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13 }}>{s.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* ── GROUP: Anatomy dropdown ── */}
            {/* ── GROUP: Anatomy dropdown ── */}
<Box sx={{ display: "flex", alignItems: "center" }}>
  <Button
    size="small"
    onClick={(e) => setAnatomyAnchor(e.currentTarget)}
    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
    startIcon={
      <Box sx={{
        width: 18, height: 18, borderRadius: 1,
        background: "linear-gradient(135deg, #fca5a5 0%, #f97316 100%)",
        border: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, color: "#fff", fontWeight: 700,
      }}>
        A
      </Box>
    }
    sx={{
      color: "#333", background: "#fff", border: "1px solid #e0e0e0",
      borderRadius: 2, textTransform: "none", fontSize: 12,
      fontWeight: 500, px: 1.5, py: 0.6, minWidth: 110,
      "&:hover": { background: "#f5f5f5", borderColor: "#ccc" },
    }}
  >
    Anatomy
  </Button>

  <Popover
    open={anatomyOpen}
    anchorEl={anatomyAnchor}
    onClose={() => { setAnatomyAnchor(null); setAnatomySearch(""); }}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    disableAutoFocus
    disableEnforceFocus
    PaperProps={{
      sx: {
        mt: 0.5,
        width: 260,
        maxHeight: 420,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        overflow: "hidden",
      },
    }}
  >
    {/* Search input */}
    <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        placeholder="Search anatomy..."
        value={anatomySearch}
        onChange={e => setAnatomySearch(e.target.value)}
        InputProps={{ sx: { fontSize: 13, borderRadius: 1.5 } }}
      />
    </Box>

    {/* Scrollable list */}
    <Box sx={{ overflowY: "auto", flex: 1 }}>

      {/* Recent searches — only when search is empty */}
      {!anatomySearch && recentAnatomy.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, color: "#999", px: 2, pt: 1, pb: 0.3, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Recent
          </Typography>
          {recentAnatomy.map(item => (
            <Box
              key={"recent-" + item.id}
              sx={{
                display: "flex", alignItems: "center", px: 1.5, py: 0.8,
                cursor: "pointer", borderRadius: 1, mx: 0.5,
                "&:hover": { background: "#f5f5f5" },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}
                onClick={() => { handleAnatomyInsert(item); setAnatomySearch(""); }}
              >
                {item.svg ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: item.svg }}
                    sx={{
                      width: 24, height: 24, flexShrink: 0, overflow: "hidden",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      "& svg": { width: "100% !important", height: "100% !important" },
                      opacity: 0.6,
                    }}
                  />
                ) : (
                  <Box component="img" src={item.svgUrl} sx={{ width: 24, height: 24, objectFit: "contain", opacity: 0.6 }} />
                )}
                <Typography sx={{ fontSize: 13, color: "#555" }}>{item.label}</Typography>
              </Box>
              <Tooltip title="Preview" arrow placement="right">
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); setAnatomyPreview(item); }}
                  sx={{ color: "#bbb", "&:hover": { color: "#1976d2", background: "#e3f2fd" }, width: 26, height: 26 }}
                >
                  <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }}>👁</Box>
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Typography sx={{ fontSize: 11, color: "#999", px: 2, pt: 0.5, pb: 0.3, textTransform: "uppercase", letterSpacing: 0.5 }}>
            All
          </Typography>
        </Box>
      )}

      {/* Filtered anatomy list */}
      {ANATOMY_IMAGES.filter(item =>
        item.label.toLowerCase().includes(anatomySearch.toLowerCase())
      ).length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "#aaa", textAlign: "center", py: 3 }}>
          No results for "{anatomySearch}"
        </Typography>
      ) : (
        ANATOMY_IMAGES
          .filter(item => item.label.toLowerCase().includes(anatomySearch.toLowerCase()))
          .map(item => (
            <Box
              key={item.id}
              sx={{
                display: "flex", alignItems: "center", px: 1.5, py: 0.8,
                cursor: "pointer", borderRadius: 1, mx: 0.5,
                "&:hover": { background: "#f5f5f5" },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}
                onClick={() => { handleAnatomyInsert(item); setAnatomySearch(""); }}
              >
                {item.svg ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: item.svg }}
                    sx={{
                      width: 28, height: 28, flexShrink: 0, overflow: "hidden",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      "& svg": { width: "100% !important", height: "100% !important" },
                    }}
                  />
                ) : (
                  <Box component="img" src={item.svgUrl} sx={{ width: 28, height: 28, objectFit: "contain" }} />
                )}
                <Typography sx={{ fontSize: 13 }}>{item.label}</Typography>
              </Box>
              <Tooltip title="Preview" arrow placement="right">
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); setAnatomyPreview(item); }}
                  sx={{ color: "#bbb", "&:hover": { color: "#1976d2", background: "#e3f2fd" }, width: 26, height: 26 }}
                >
                  <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }}>👁</Box>
                </IconButton>
              </Tooltip>
            </Box>
          ))
      )}
    </Box>
  </Popover>
</Box>

            {/* ── GROUP: Colors dropdown ── */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                size="small"
                onClick={(e) => setColorAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                startIcon={<Box sx={{ width: 18, height: 18, borderRadius: "50%", background: color, border: "1px solid #ccc" }} />}
                sx={{
                  color: "#333", background: "#fff", border: "1px solid #e0e0e0",
                  borderRadius: 2, textTransform: "none", fontSize: 12,
                  fontWeight: 500, px: 1.5, py: 0.6, minWidth: 100,
                  "&:hover": { background: "#f5f5f5" },
                }}
              >
                Color
              </Button>

              <Menu
                anchorEl={colorAnchor}
                open={colorOpen}
                onClose={() => setColorAnchor(null)}
                PaperProps={{ sx: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 2, mt: 0.5, p: 1, width: 200 } }}
              >
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                  {COLORS.map(c => (
                    <Box
                      key={c}
                      onClick={() => { setColor(c); setColorAnchor(null); }}
                      sx={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: c, cursor: "pointer",
                        border: color === c ? "3px solid #1976d2" : "1px solid #ddd",
                        transition: "transform 0.1s",
                        "&:hover": { transform: "scale(1.1)" },
                      }}
                    />
                  ))}
                  <Box
                    component="label"
                    sx={{
                      width: 32, height: 32, borderRadius: "50%", cursor: "pointer",
                      border: "1px dashed #999", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: 20, color: "#999", position: "relative",
                    }}
                  >
                    +
                    <input
                      type="color"
                      value={color}
                      onChange={e => { setColor(e.target.value); setColorAnchor(null); }}
                      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                    />
                  </Box>
                </Box>
              </Menu>
            </Box>

            {/* ── GROUP: Stroke size dropdown ── */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                size="small"
                onClick={(e) => setSizeAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                startIcon={<Box sx={{ width: strokeSize + 8, height: strokeSize + 8, borderRadius: "50%", background: color }} />}
                sx={{
                  color: "#333", background: "#fff", border: "1px solid #e0e0e0",
                  borderRadius: 2, textTransform: "none", fontSize: 12,
                  fontWeight: 500, px: 1.5, py: 0.6, minWidth: 100,
                  "&:hover": { background: "#f5f5f5" },
                }}
              >
                Size: {strokeSize}px
              </Button>

              <Menu
                anchorEl={sizeAnchor}
                open={sizeOpen}
                onClose={() => setSizeAnchor(null)}
                PaperProps={{ sx: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 2, mt: 0.5, p: 1, width: 220 } }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                  {STROKE_SIZES.map(s => (
                    <Tooltip key={s} title={`${s}px`} arrow>
                      <Box
                        onClick={() => { setStrokeSize(s); setSizeAnchor(null); }}
                        sx={{
                          width: s + 12, height: s + 12, borderRadius: "50%",
                          background: strokeSize === s ? color : "#e0e0e0",
                          cursor: "pointer",
                          border: strokeSize === s ? "2px solid #1976d2" : "1px solid #ccc",
                          transition: "transform 0.1s",
                          "&:hover": { transform: "scale(1.15)" },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Menu>
            </Box>

            {/* ── Fullscreen button ── */}
            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} arrow>
              <IconButton
                size="small"
                onClick={toggleFullscreen}
                sx={{
                  color: isFullscreen ? "#1976d2" : "#666",
                  background: isFullscreen ? "#e3f2fd" : "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1.5, width: 36, height: 32,
                  "&:hover": { background: "#f0f0f0" },
                }}
              >
                {isFullscreen ? <FullscreenExit sx={{ fontSize: 18 }} /> : <Fullscreen sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>

          </Box>
        )}

        {/* ── Canvas area ── */}
        <Box sx={{ flex: 1, position: "relative", overflow: "hidden", background: "#e8eaed" }}>
          <canvas ref={canvasRef} style={{ display: "block" }} />

          {/* Loading overlay */}
          {isLoading && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(216, 214, 214, 0.4)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1301,
              }}
            >
              {showLoadingImage()}
            </div>
          )}

          {/* ── Zoom controls ── */}
          <Box sx={{
            position: "absolute", bottom: 16, right: 16, zIndex: 50,
            display: "flex", alignItems: "center", gap: 0.5,
            background: "rgba(255,255,255,0.96)",
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            px: 0.5, py: 0.4,
          }}>
            <Tooltip title="Zoom out (Ctrl + −)" arrow placement="top">
              <IconButton size="small" onClick={zoomOut} disabled={zoomPct <= Math.round(ZOOM_MIN * 100)} sx={{ width: 30, height: 30 }}>
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reset zoom (Ctrl + 0)" arrow placement="top">
              <Typography
                onClick={resetView}
                sx={{ fontSize: 12, fontWeight: 700, color: "#444", minWidth: 44, textAlign: "center", cursor: "pointer", px: 0.5, py: 0.2, borderRadius: 1, "&:hover": { background: "#f0f0f0" } }}
              >
                {zoomPct}%
              </Typography>
            </Tooltip>

            <Tooltip title="Zoom in (Ctrl + +)" arrow placement="top">
              <IconButton size="small" onClick={zoomIn} disabled={zoomPct >= Math.round(ZOOM_MAX * 100)} sx={{ width: 30, height: 30 }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Box sx={{ width: "1px", height: 20, background: "#e0e0e0", mx: 0.3 }} />
          </Box>

          {/* Exit fullscreen FAB */}
          {isFullscreen && !readOnly && (
            <Tooltip title="Exit Fullscreen" arrow>
              <IconButton
                onClick={toggleFullscreen}
                sx={{
                  position: "absolute", bottom: 12, left: 12, zIndex: 51,
                  background: "rgba(255,255,255,0.9)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": { background: "#fff" },
                }}
              >
                <FullscreenExit />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Save Name Dialog ── */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save Whiteboard</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name to identify this whiteboard in the records list.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Whiteboard Name"
            placeholder="e.g. Shoulder anatomy diagram"
            value={saveName}
            onChange={(e) => {
              setSaveName(e.target.value);
              if (saveNameError) setSaveNameError("");
            }}
            error={Boolean(saveNameError)}
            helperText={saveNameError}
            onKeyDown={(e) => { if (e.key === "Enter") handleSaveConfirm(); }}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveConfirm} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── Anatomy Preview Dialog ── */}
<Dialog
  open={Boolean(anatomyPreview)}
  onClose={() => setAnatomyPreview(null)}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <Typography fontWeight={500}>{anatomyPreview?.label}</Typography>
  </DialogTitle>
  <DialogContent dividers sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320, background: "#f8f9fb" }}>
    {anatomyPreview?.svg ? (
      <Box
        dangerouslySetInnerHTML={{ __html: anatomyPreview.svg }}
        sx={{
          width: "100%", maxWidth: 360,
          "& svg": { width: "100% !important", height: "auto !important" },
        }}
      />
    ) : anatomyPreview?.svgUrl ? (
      <Box component="img" src={anatomyPreview.svgUrl} sx={{ width: "100%", maxWidth: 360, objectFit: "contain" }} />
    ) : null}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setAnatomyPreview(null)} variant="outlined" color="inherit">
      Close
    </Button>
    <Button
      onClick={() => {
        setAnatomyPreview(null);
        handleAnatomyInsert(anatomyPreview);
      }}
      variant="contained"
      color="primary"
    >
      Insert on Canvas
    </Button>
  </DialogActions>
</Dialog>
    </>
  );
}

export default Drawing;